import React, { useEffect, useState } from 'react';
import './mcp.css';
import { Alert, InputNumber, Radio, Spin, Tag } from 'antd';
import { CreditCardOutlined, InfoCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { authHeader } from '../../../utils/api';
import McpHeader from '../../../components/mcp/McpHeader';

const PLAN_TIERS = ['starter', 'grow', 'scale'];

const TIER_NAMES = {
  starter: 'Starter',
  grow: 'Grow',
  scale: 'Scale',
};

const PLANS = {
  starter: { name: 'Starter', monthlyUsd: 149, annualMonthlyUsd: 127, includedVehicles: 3, overagePerVehicleUsd: 39 },
  grow: { name: 'Grow', monthlyUsd: 299, annualMonthlyUsd: 254, includedVehicles: 8, overagePerVehicleUsd: 34 },
  scale: { name: 'Scale', monthlyUsd: 549, annualMonthlyUsd: 467, includedVehicles: 20, overagePerVehicleUsd: 25 },
};

function quote(tier, interval, qty) {
  const plan = PLANS[tier];
  if (!plan) return { perMonthUsd: 0 };
  const base = interval === 'annual' ? plan.annualMonthlyUsd : plan.monthlyUsd;
  const excess = Math.max(0, qty - plan.includedVehicles);
  return { perMonthUsd: base + excess * plan.overagePerVehicleUsd };
}

function recommendTier(count) {
  if (count <= PLANS.starter.includedVehicles) return 'starter';
  if (count <= PLANS.grow.includedVehicles) return 'grow';
  return 'scale';
}

function perMo(usd) {
  return `$${usd.toLocaleString()}/mo`;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function describePlanChange({ currentTier, currentInterval, currentSlots, nextTier, nextInterval, nextSlots }) {
  if (!currentTier) return { timing: 'immediate', label: 'Switch to tiered pricing, charged today and prorated.' };
  if (nextTier !== currentTier || nextInterval !== currentInterval) {
    const cur = quote(currentTier, currentInterval, currentSlots).perMonthUsd;
    const nxt = quote(nextTier, nextInterval, nextSlots).perMonthUsd;
    if (nxt >= cur) {
      return { timing: 'immediate', label: `Upgraded to ${TIER_NAMES[nextTier]} (${nextInterval}), charged today.` };
    }
    return { timing: 'renewal', label: `Downgraded to ${TIER_NAMES[nextTier]} (${nextInterval}) at renewal.` };
  }
  if (nextSlots !== currentSlots) {
    if (nextSlots > currentSlots) return { timing: 'immediate', label: `Adding ${nextSlots - currentSlots} slot(s), charged today.` };
    return { timing: 'renewal', label: `Removing ${currentSlots - nextSlots} slot(s) at renewal.` };
  }
  return { timing: 'none', label: '' };
}

const McpBilling = () => {
  const [subscription, setSubscription] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pickedTier, setPickedTier] = useState(null);
  const [pickedInterval, setPickedInterval] = useState(null);
  const [targetQty, setTargetQty] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [changeMsg, setChangeMsg] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch('/api/client/billing/subscription', { headers: authHeader() })
        .then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/vehicles', { headers: authHeader() })
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([bill, v]) => {
      setSubscription(bill);
      const vlist = Array.isArray(v) ? v : v.vehicles || [];
      setVehicles(vlist);
      setLoading(false);
    }).catch(err => {
      setError(err.message);
      setLoading(false);
    });
  }, []);

  const currentTier = (subscription?.plan || subscription?.planKey || '').toLowerCase();
  const isLegacy = currentTier && !PLANS[currentTier];
  const currentInterval = subscription?.billing_interval || subscription?.interval || 'monthly';
  const currentQty = subscription?.vehicle_limit || subscription?.seats || (PLANS[currentTier] ? PLANS[currentTier].includedVehicles : 1) || 1;
  const activeCount = vehicles.filter(v => v.is_active || v.status === 'active').length;

  const sel = { tier: pickedTier || (PLANS[currentTier] ? currentTier : recommendTier(Math.max(1, activeCount || currentQty))) || 'grow', interval: pickedInterval || currentInterval };
  const qty = targetQty ?? Math.max(PLANS[sel.tier]?.includedVehicles || 1, currentQty);
  const plan = PLANS[sel.tier];
  const excess = Math.max(0, qty - (plan?.includedVehicles || 1));
  const baseMonthly = sel.interval === 'annual' ? plan?.annualMonthlyUsd : plan?.monthlyUsd;
  const overageMonthly = excess * (plan?.overagePerVehicleUsd || 0);
  const currentQuote = currentTier && PLANS[currentTier] ? quote(currentTier, currentInterval, currentQty) : null;
  const nextQuote = quote(sel.tier, sel.interval, qty);
  const annualSavings =
    sel.interval === 'monthly' && plan
      ? nextQuote.perMonthUsd * 12 - quote(sel.tier, 'annual', qty).perMonthUsd * 12
      : 0;

  const dirty = pickedTier !== null || pickedInterval !== null || targetQty !== null;

  const change = describePlanChange({
    currentTier: currentTier && PLANS[currentTier] ? currentTier : null,
    currentInterval,
    currentSlots: currentQty,
    nextTier: sel.tier,
    nextInterval: sel.interval,
    nextSlots: qty,
  });

  const pendingTier = (subscription?.pending_plan_tier || '').toLowerCase() || null;
  const pendingSlots = subscription?.pending_downgrade_to ?? null;
  const pendingInterval = subscription?.pending_billing_interval || currentInterval;

  async function confirmChange() {
    if (!dirty) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/client/billing/change-plan', {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: sel.tier }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error?.message || data.message || 'Could not change your plan.');
      setChangeMsg('Plan updated! Changes will reflect within a few minutes.');
      setPickedTier(null); setPickedInterval(null); setTargetQty(null);
      const fresh = await fetch('/api/client/billing/subscription', { headers: authHeader() })
        .then(r => r.ok ? r.json() : null).catch(() => null);
      if (fresh) setSubscription(fresh);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function openPortal() {
    setSubmitting(true);
    try {
      const res = await fetch('/api/client/billing/portal', { headers: authHeader() });
      const body = await res.json();
      if (body.url) window.location.href = body.url;
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="mcp-page" style={{ padding: 0 }}>
        <McpHeader title="Billing" />
        <div style={{ textAlign: 'center', paddingTop: 80 }}>
          <Spin size="large" tip="Loading billing info…" />
        </div>
      </div>
    );
  }

  if (true || subscription?.comp) {
    return (
      <div className="mcp-page" style={{ padding: 0 }}>
        <McpHeader title="Billing" />
        <div className="p-6 md:p-8">
          <div className="mcp-card3d p-6">
            <InfoCircleOutlined style={{ fontSize: 28, color: 'var(--brand)', marginBottom: 12 }} />
            <div className="font-bold text-ink mb-2">Your account is comped.</div>
            <p className="text-sm text-ink-200 m-0">Unlimited vehicles, full access, no billing on this account. Nothing to manage here.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!subscription || (!currentTier && !isLegacy)) {
    return (
      <div className="mcp-page" style={{ padding: 0 }}>
        <McpHeader title="Billing" />
        <div className="p-6 md:p-8">
          <div className="rounded-2xl border border-paper-300 bg-white p-5 shadow-card">
            <p className="text-sm text-ink-100">
              No subscription found. Pick a plan to get started.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mcp-page" style={{ padding: 0 }}>
      <McpHeader title="Billing" />

      <div className="mx-auto w-full max-w-4xl p-6 md:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-1">
          <h2 className="font-display text-2xl font-bold text-ink">Billing</h2>
          <p className="text-sm text-ink-200">Manage your subscription, slots, and invoices.</p>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            className="rounded-xl border-ember"
          />
        )}
        {changeMsg && (
          <Alert
            message={changeMsg}
            type="success"
            showIcon
            closable
            onClose={() => setChangeMsg(null)}
            className="rounded-xl"
          />
        )}

        {/* Current plan */}
        <section className="rounded-2xl border border-paper-300 bg-white p-5 shadow-card sm:p-6">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-200">Current plan</p>
            <Tag color={subscription?.subscription_status === 'active' || subscription?.status === 'active' ? 'success' : 'error'} className="rounded-full font-semibold text-xs">
              {subscription?.subscription_status || subscription?.status || 'inactive'}
            </Tag>
          </div>
          <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-2xl font-bold text-ink">
                {PLANS[currentTier] ? TIER_NAMES[currentTier] : currentTier ? (TIER_NAMES[currentTier] || currentTier) : 'Legacy per-vehicle'}
              </h2>
              <p className="mt-1 text-sm text-ink-200">
                {currentInterval === 'annual' ? 'Annual billing · save 15% · ' : 'Monthly billing · '}
                Renews {formatDate(subscription?.nextBilling || subscription?.next_billing_date)}
              </p>
            </div>
            {currentQuote && (
              <div className="text-right">
                <span className="font-display text-3xl font-bold text-ink">${currentQuote.perMonthUsd.toLocaleString()}</span>
                <span className="text-sm text-ink-200">/mo</span>
              </div>
            )}
          </div>
          <p className="mt-3 border-t border-paper-300 pt-3 text-sm text-ink-100">
            {PLANS[currentTier] ? (
              <>
                {PLANS[currentTier].includedVehicles} included
                {currentQty > PLANS[currentTier].includedVehicles && (
                  <> + {currentQty - PLANS[currentTier].includedVehicles} extra × ${PLANS[currentTier].overagePerVehicleUsd}/mo</>
                )} = {currentQty} slot{currentQty === 1 ? '' : 's'}
              </>
            ) : (
              <>{currentQty} slot{currentQty === 1 ? '' : 's'}</>
            )}
            {' · tracking '} {activeCount} of {currentQty}
          </p>

          {isLegacy && (
            <div className="mt-4 flex items-start gap-2.5 border-l-[3px] border-brand py-0.5 pl-3">
              <InfoCircleOutlined className="mt-0.5 shrink-0 text-brand" style={{ fontSize: 15 }} />
              <p className="text-sm text-ink-50">
                You're on legacy per-vehicle pricing. Pick a plan below to move to tiered pricing — the switch applies immediately and is prorated.
              </p>
            </div>
          )}

          {pendingSlots != null && (
            <div className="mt-4 flex items-start gap-2.5 border-l-[3px] border-sun py-0.5 pl-3">
              <ClockCircleOutlined className="mt-0.5 shrink-0 text-sun" style={{ fontSize: 15 }} />
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-200">Scheduled change</p>
                <p className="mt-1 text-sm text-ink-50">
                  {pendingTier ? `Moves to ${TIER_NAMES[pendingTier]} (${pendingInterval}) with ${pendingSlots} slot${pendingSlots === 1 ? '' : 's'}` : `Drops to ${pendingSlots} slot${pendingSlots === 1 ? '' : 's'}`} on {formatDate(subscription?.pending_downgrade_effective_at)}.
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Change plan */}
        <section className="space-y-4">
          <div>
            <h3 className="font-display text-xl font-bold text-ink">Change plan</h3>
            <p className="mt-1 text-sm text-ink-200">Upgrades and interval changes are charged today, prorated. Downgrades land at renewal — no refunds.</p>
          </div>

          <Radio.Group
            value={sel.interval}
            onChange={e => setPickedInterval(e.target.value)}
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="monthly">Monthly</Radio.Button>
            <Radio.Button value="annual">Annual · save 15%</Radio.Button>
          </Radio.Group>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {PLAN_TIERS.map(tier => {
              const p = PLANS[tier];
              const selected = sel.tier === tier;
              const recommended = activeCount > 0 && recommendTier(activeCount) === tier;
              return (
                <button
                  key={tier}
                  type="button"
                  onClick={() => { setPickedTier(tier); setTargetQty(null); }}
                  disabled={submitting}
                  className={`rounded-2xl border-2 p-5 text-left transition-colors ${
                    selected
                      ? 'border-brand bg-brand-50'
                      : 'border-paper-300 bg-white hover:border-ink-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-ink">{p.name}</span>
                    {recommended && <Tag className="rounded-full text-[10px] font-semibold">Recommended</Tag>}
                  </div>
                  <div className="mt-1 font-display text-2xl font-bold text-ink">
                    ${sel.interval === 'annual' ? p.annualMonthlyUsd : p.monthlyUsd}
                    <span className="ml-1 text-sm font-normal text-ink-200">/mo</span>
                  </div>
                  <div className="mt-2 text-xs text-ink-200">{p.includedVehicles} vehicles included</div>
                </button>
              );
            })}
          </div>

          <div className="rounded-2xl border border-paper-300 bg-white p-5 shadow-card">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="font-semibold text-sm text-ink">Fleet slots</div>
                <div className="text-xs text-ink-200">
                  {plan.includedVehicles} included · extra at ${plan.overagePerVehicleUsd}/mo each
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="mcp-btn-ghost h-8 w-8 items-center justify-center p-0"
                  disabled={qty <= plan.includedVehicles || submitting}
                  onClick={() => setTargetQty(Math.max(plan.includedVehicles, qty - 1))}
                >
                  −
                </button>
                <InputNumber
                  min={plan.includedVehicles}
                  max={500}
                  value={qty}
                  onChange={v => setTargetQty(v)}
                  style={{ width: 70 }}
                  controls={false}
                />
                <button
                  type="button"
                  className="mcp-btn-ghost h-8 w-8 items-center justify-center p-0"
                  disabled={qty >= 500 || submitting}
                  onClick={() => setTargetQty(qty + 1)}
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-paper-300 bg-white p-5 shadow-card sm:p-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-200">Order summary</div>

            <dl className="mt-3 divide-y divide-paper-200 font-mono text-sm">
              <div className="flex items-baseline justify-between gap-4 py-2.5">
                <dt className="text-ink-100">
                  <span className="font-sans font-semibold text-ink">{TIER_NAMES[sel.tier]} plan</span>
                  <span className="ml-2 text-[12px] text-ink-200">{plan.includedVehicles} vehicles included</span>
                </dt>
                <dd className="shrink-0 tabular-nums text-ink">{perMo(baseMonthly)}</dd>
              </div>

              <div className="flex items-baseline justify-between gap-4 py-2.5">
                <dt className="text-ink-100">
                  <span className="font-sans font-semibold text-ink">Extra vehicles</span>
                  <span className="ml-2 text-[12px] text-ink-200">
                    {excess > 0 ? `${excess} additional × $${plan.overagePerVehicleUsd}/mo` : 'none'}
                  </span>
                </dt>
                <dd className="shrink-0 tabular-nums text-ink">{excess > 0 ? `+${perMo(overageMonthly)}` : '$0/mo'}</dd>
              </div>

              <div className="flex items-baseline justify-between gap-4 border-t border-paper-300 py-3">
                <dt className="font-sans font-semibold text-ink">
                  {change.timing === 'immediate' ? 'New monthly total' : 'Monthly total'}
                  {sel.interval === 'annual' && (
                    <span className="ml-2 text-[12px] font-normal text-ink-200">billed annually</span>
                  )}
                </dt>
                <dd className="shrink-0 tabular-nums">
                  <span className="inline-flex items-baseline gap-1 rounded-lg bg-sun-100 px-3 py-1">
                    <span className="font-display text-2xl font-bold text-ink">${nextQuote.perMonthUsd.toLocaleString()}</span>
                    <span className="text-xs text-ink-100">/mo</span>
                  </span>
                </dd>
              </div>
            </dl>

            {change.timing !== 'none' && (
              <div className="mt-4 border-t border-paper-300 pt-3 font-mono text-[13px]">
                <span className="text-ink-200">{change.timing === 'renewal' ? 'At renewal: ' : 'Charged today: '}</span>
                <span className="text-ink">{change.label}</span>
              </div>
            )}

            {change.timing === 'none' && (
              <div className="mt-4 border-t border-paper-300 pt-3 text-[13px] text-ink-200">This is your current plan.</div>
            )}

            {sel.interval === 'monthly' && annualSavings > 0 && (
              <button
                type="button"
                onClick={() => setPickedInterval('annual')}
                disabled={submitting}
                className="mt-4 flex w-full items-center justify-between gap-3 rounded-xl border border-paper-300 bg-paper-50 px-3.5 py-2.5 text-left text-[13px] transition-colors hover:border-ink-300"
              >
                <span className="text-ink">
                  Save <span className="font-semibold">${annualSavings.toLocaleString()}/yr</span> by switching to annual billing.
                </span>
                <span className="shrink-0 text-xs font-semibold text-ink-100">Switch to annual →</span>
              </button>
            )}

            <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-t border-paper-300 pt-4">
              {dirty && (
                <button type="button" className="mcp-btn-text" onClick={() => { setPickedTier(null); setPickedInterval(null); setTargetQty(null); }} disabled={submitting}>
                  Reset
                </button>
              )}
              <button
                type="button"
                className="mcp-btn-sun"
                onClick={confirmChange}
                disabled={submitting || !dirty}
              >
                {submitting ? 'Saving…' : 'Confirm change'}
              </button>
            </div>
          </div>
        </section>

        <div>
          <button
            type="button"
            className="mcp-btn-ghost"
            onClick={openPortal}
            disabled={submitting}
          >
            <CreditCardOutlined /> Manage card + invoices (Stripe Portal) →
          </button>
        </div>
      </div>
    </div>
  );
};

export default McpBilling;
